// ═══════════════════════════════════════════════════════════════════════════
// RUN 21: Footer Template Cleanup
// ═══════════════════════════════════════════════════════════════════════════
import { safeAlert, reportResult, rangeDetail, RunDetail, SRS_NAV_PREFIX } from '../runs/common';
import { getConfig, RemoveFooterConfig } from '../taskpane/configService';

function makeRevIdBase(): number {
  return Date.now() & 0x0FFFFFFF;
}

const FOOTER_TYPES: { type: Word.HeaderFooterType; name: string }[] = [
  { type: Word.HeaderFooterType.primary,   name: "Primary"   },
  { type: Word.HeaderFooterType.firstPage, name: "FirstPage" },
  { type: Word.HeaderFooterType.evenPages, name: "EvenPages" },
];

// ── Build a <w:del>-wrapped version of a single run block ──
function wrapRunInDel(runBlock: string, revId: number, author: string): string {
  const deletedRun = runBlock
    .replace(/<w:t(\s[^>]*)?>([\s\S]*?)<\/w:t>/g,               '<w:delText$1>$2</w:delText>')
    .replace(/<w:instrText(\s[^>]*)?>([\s\S]*?)<\/w:instrText>/g, '<w:delInstrText$1>$2</w:delInstrText>');

  const now = new Date().toISOString().split(".")[0] + "Z";
  return `<w:del w:id="${revId}" w:author="${author}" w:date="${now}">${deletedRun}</w:del>`;
}

// ── Inject a zero-width navigation bookmark into a <w:p> block ──
function injectBookmark(pBlock: string, bmId: number, bmName: string): string {
  const bmXml =
    `<w:bookmarkStart w:id="${bmId}" w:name="${bmName}"/>` +
    `<w:bookmarkEnd   w:id="${bmId}"/>`;

  return pBlock.includes("</w:pPr>")
    ? pBlock.replace("</w:pPr>", `</w:pPr>${bmXml}`)
    : pBlock.replace(/(<w:p\b[^>]*>)/, `$1${bmXml}`);
}

// ── Return type for buildRunReplacements ──
interface RunProcessResult {
  patchedBlock:      string;
  runsDeletedInPara: number;
}

// ── Scan all <w:r> runs in a paragraph OOXML block and wrap deletable ones ──
function buildRunReplacements(
  paraBlock:        string,
  preserveText:     string,
  revIdBase:        number,
  runsDeletedSoFar: number,
  author:           string,
): RunProcessResult {
  const runRegex = /<w:r\b[^>]*>[\s\S]*?<\/w:r>/g;
  const replacements: { original: string; replacement: string }[] = [];
  let paraRunsDeleted = 0;
  let runMatch: RegExpExecArray | null;

  while ((runMatch = runRegex.exec(paraBlock)) !== null) {
    const runBlock = runMatch[0];

    const tMatches = runBlock.match(/<w:t(?:\s[^>]*)?>([^<]*)<\/w:t>/g) ?? [];
    const runText  = tMatches
      .map(t => t.replace(/<[^>]+>/g, ""))
      .join("")
      .trim()
      .toLowerCase();

    if (runText === preserveText.toLowerCase()) {
      console.log(`    Run "${runText}" — preserved`);
      continue;
    }

    const revId = revIdBase + runsDeletedSoFar + paraRunsDeleted;
    replacements.push({
      original:    runBlock,
      replacement: wrapRunInDel(runBlock, revId, author),
    });
    paraRunsDeleted++;
  }

  let patchedBlock = paraBlock;
  for (const rep of replacements) {
    patchedBlock = patchedBlock.replace(rep.original, rep.replacement);
  }

  return { patchedBlock, runsDeletedInPara: paraRunsDeleted };
}

// ── Return type for processFooterParagraph ──
interface ParagraphProcessResult {
  runsDeleted:   number;
  runsPreserved: number;
  bmCounter:     number;
}

// ── Options object for processFooterParagraph (S107: keeps params ≤ 7) ──
interface ParagraphProcessOptions {
  c:            RemoveFooterConfig;
  revIdBase:    number;
  runsDeleted:  number;
  bmCounter:    number;
  changedParas: RunDetail[];
}

// ── Process a single paragraph inside a footer ──
async function processFooterParagraph(
  context:    Word.RequestContext,
  para:       Word.Paragraph,
  paraIndex:  number,
  paraLabel:  string,
  opts:       ParagraphProcessOptions,
): Promise<ParagraphProcessResult> {
  para.load("text");
  await context.sync();

  const { c, revIdBase, changedParas } = opts;
  const { runsDeleted, bmCounter }     = opts;

  const paraText    = (para.text ?? "").trim().toLowerCase();
  const displayText = para.text.trim().substring(0, 50);

  // Entire paragraph matches preserveText → skip
  if (paraText === c.preserveText.toLowerCase()) {
    console.log(`  [P${paraIndex + 1}] "${c.preserveText.toUpperCase()}" — preserved ✓`);
    return { runsDeleted, runsPreserved: 1, bmCounter };
  }

  // Empty paragraph → skip
  if (paraText.length === 0) {
    console.log(`  [P${paraIndex + 1}] (empty) — skipped`);
    return { runsDeleted, runsPreserved: 0, bmCounter };
  }

  console.log(`  [P${paraIndex + 1}] "${para.text.trim().substring(0, 60)}" — processing via OOXML`);

  const paraOoxmlResult = para.getOoxml();
  await context.sync();
  const fullPackage = paraOoxmlResult.value;

  const paraMatch = fullPackage.match(/<w:p\b[\s\S]*?<\/w:p>/);
  if (!paraMatch) {
    console.log(`    ⚠ No <w:p> block found — skipped`);
    return { runsDeleted, runsPreserved: 0, bmCounter };
  }

  const paraBlock      = paraMatch[0];
  const paraBlockStart = fullPackage.indexOf(paraBlock);

  const { patchedBlock: afterDel, runsDeletedInPara } = buildRunReplacements(
    paraBlock,
    c.preserveText,
    revIdBase,
    runsDeleted,
    c.delAuthor,
  );

  if (runsDeletedInPara === 0) {
    return { runsDeleted, runsPreserved: 0, bmCounter };
  }

  const bmName       = `${SRS_NAV_PREFIX}r21_${revIdBase}_${bmCounter}`;
  const bmId         = revIdBase + 500 + bmCounter;
  const patchedBlock = injectBookmark(afterDel, bmId, bmName);

  const patchedPackage =
    fullPackage.substring(0, paraBlockStart) +
    patchedBlock +
    fullPackage.substring(paraBlockStart + paraBlock.length);

  context.document.changeTrackingMode = Word.ChangeTrackingMode.off;
  await context.sync();

  para.insertOoxml(patchedPackage, "Replace");
  await context.sync();

  changedParas.push(rangeDetail(bmName, `${paraLabel}: "${displayText}"`, "Footer"));
  console.log(`    ${runsDeletedInPara} run(s) deleted, nav bookmark "${bmName}" injected ✓`);

  return {
    runsDeleted:   runsDeleted + runsDeletedInPara,
    runsPreserved: 0,
    bmCounter:     bmCounter + 1,
  };
}

// ── Mutable state bag threaded through processFooter calls ──
interface FooterState {
  runsDeleted:    number;
  runsPreserved:  number;
  parasProcessed: number;
  bmCounter:      number;
}

// ── Options object for processFooter (S107: keeps params ≤ 7) ──
interface FooterProcessOptions {
  c:            RemoveFooterConfig;
  revIdBase:    number;
  state:        FooterState;
  changedParas: RunDetail[];
}

// ── Process all paragraphs in one footer type for one section ──
async function processFooter(
  context:      Word.RequestContext,
  section:      Word.Section,
  sectionIndex: number,
  ft:           { type: Word.HeaderFooterType; name: string },
  opts:         FooterProcessOptions,
): Promise<void> {
  const { c, revIdBase, state, changedParas } = opts;

  let footerBody: Word.Body;
  try {
    footerBody = section.getFooter(ft.type);
    footerBody.load("paragraphs");
    await context.sync();
  } catch (e) {
    // Missing footer is expected for some section/type combinations
    console.log(`  Section ${sectionIndex + 1} ${ft.name}: No footer — skipped (${(e as Error).message})`);
    return;
  }

  const paragraphs = footerBody.paragraphs;
  paragraphs.load("items");
  await context.sync();

  if (paragraphs.items.length === 0) {
    console.log(`  Section ${sectionIndex + 1} ${ft.name}: Empty — skipped`);
    return;
  }

  console.log(`\n── Section ${sectionIndex + 1} ${ft.name} Footer (${paragraphs.items.length} para(s)) ──`);

  for (let p = 0; p < paragraphs.items.length; p++) {
    const paraLabel = `Section ${sectionIndex + 1} – ${ft.name} Footer`;
    state.parasProcessed++;

    const result = await processFooterParagraph(
      context,
      paragraphs.items[p],
      p,
      paraLabel,
      {
        c,
        revIdBase,
        runsDeleted:  state.runsDeleted,
        bmCounter:    state.bmCounter,
        changedParas,
      },
    );

    state.runsDeleted    = result.runsDeleted;
    state.runsPreserved += result.runsPreserved;
    state.bmCounter      = result.bmCounter;
  }
}

// ── Main entry point ──
export async function run21(): Promise<void> {
  const c = getConfig().run21_removeFooter;

  await Word.run(async (context) => {
    try {
      console.log("═".repeat(70));
      console.log("RUN 21: Footer Template Cleanup");
      console.log("═".repeat(70));

      const sections = context.document.sections;
      sections.load("items");
      await context.sync();

      const totalSections = sections.items.length;
      console.log(`Total sections: ${totalSections}\n`);

      const revIdBase    = makeRevIdBase();
      const changedParas: RunDetail[] = [];
      const state: FooterState = {
        runsDeleted:    0,
        runsPreserved:  0,
        parasProcessed: 0,
        bmCounter:      0,
      };

      console.log(`Revision ID base: ${revIdBase}`);

      for (let s = 0; s < totalSections; s++) {
        for (const ft of FOOTER_TYPES) {
          await processFooter(context, sections.items[s], s, ft, {
            c,
            revIdBase,
            state,
            changedParas,
          });
        }
      }

      context.document.changeTrackingMode = Word.ChangeTrackingMode.off;
      await context.sync();

      console.log("\n" + "═".repeat(70));
      console.log(`Paragraphs processed: ${state.parasProcessed}`);
      console.log(`Runs deleted:         ${state.runsDeleted}`);
      console.log(`Runs preserved (${c.preserveText.toUpperCase()}): ${state.runsPreserved}`);
      console.log("✓ Footer Template Cleanup complete!");
      console.log("═".repeat(70));

      if (state.runsDeleted > 0) {
        reportResult('Remove Footer Complete', [
          { label: 'Footer paragraphs processed', count: state.parasProcessed },
          { label: 'Runs marked for deletion',    count: state.runsDeleted    },
          ...changedParas,
        ]);
      } else {
        reportResult('Remove Footer Complete', [
          {
            label: 'No changes needed',
            note:  `No template content found to remove. ${c.preserveText.toUpperCase()} runs preserved: ${state.runsPreserved}`,
          },
        ]);
      }

    } catch (error) {
      console.error("Error in run21:", error);
      console.error("Message:", (error as Error).message);
      safeAlert("An error occurred during Footer Template Cleanup.\nCheck console (F12) for details.");
    }
  });
}